// Purpose: admin dashboard with five tiles + 60s poll per §14 Q3. No SSE.
// Tiles: routing jobs by status, Graph circuit state, audit log size,
// DLQ size + oldest age, pending approvals count.
//
// Phase 7.5 port: MetricTiles grid at top for headline stats +
// Card-wrapped sections for the chart/list tiles. Preserves the fetch +
// polling logic.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardResponse } from '../../api/admin-dashboard.ts';
import { fetchDashboard } from '../../api/admin-dashboard.ts';
import { formatRelative } from '../../lib/format.ts';
import { Gauge } from '../../components/charts/Gauge.tsx';
import { TimeSeriesBar } from '../../components/charts/TimeSeriesBar.tsx';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import { Badge, Card, CardHeader, CardTitle, MetricTile } from '../../components/ui/index.ts';

const POLL_MS = 60_000;

function cbBadgeTone(state: string): 'success' | 'warning' | 'danger' {
  if (state === 'closed') return 'success';
  if (state === 'half-open') return 'warning';
  return 'danger';
}

export function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await fetchDashboard();
        if (!cancelled) {
          setData(resp);
          setError(null);
          setLastUpdated(new Date());
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    };
    void load();
    const id = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!data && !error) return <p className="text-body-m text-fg-muted">Loading dashboard…</p>;
  if (error) return <p className="text-body-m text-status-danger">{error}</p>;
  if (!data) return null;

  const tiles = data.tiles;
  const routingStatuses = ['PENDING', 'ACTIVE', 'COMPLETED', 'DEAD'];
  const routingBars = routingStatuses.map((s) => ({
    label: s,
    value: tiles.routing24hByStatus[s] ?? 0,
  }));
  const cb = tiles.routingSnapshot?.circuitBreakers;
  const cert = tiles.routingSnapshot?.cert;
  const certTone: 'danger' | 'warning' | 'success' | undefined = cert
    ? cert.daysRemaining < 14
      ? 'danger'
      : cert.daysRemaining < 30
        ? 'warning'
        : 'success'
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Admin dashboard"
        subtitle={`Last updated ${lastUpdated ? formatRelative(lastUpdated) : '—'}`}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/admin/approvals" className="block">
          <MetricTile
            label="Pending approvals"
            value={tiles.pendingApprovals}
            tone="info"
            footnote="click to review"
          />
        </Link>
        <Link to="/admin/audit" className="block">
          <MetricTile
            label="Audit log size"
            value={tiles.auditRowCount.toLocaleString()}
            tone="neutral"
            footnote="rows in chain"
          />
        </Link>
        <Link to="/admin/routing" className="block">
          <MetricTile
            label="DLQ"
            value={tiles.routingSnapshot?.dead ?? 0}
            tone={(tiles.routingSnapshot?.dead ?? 0) > 0 ? 'warning' : 'neutral'}
            footnote={
              tiles.routingSnapshot?.oldestDeadAgeSeconds == null
                ? `Completed 24h: ${tiles.routingSnapshot?.completed24h ?? 0}`
                : `Oldest ${Math.floor(tiles.routingSnapshot.oldestDeadAgeSeconds / 60)} min · Completed 24h: ${tiles.routingSnapshot?.completed24h ?? 0}`
            }
          />
        </Link>
        <MetricTile
          label="Graph cert"
          value={cert ? `${cert.daysRemaining}d` : '—'}
          tone={certTone ?? 'neutral'}
          footnote={
            cert ? `Expires ${new Date(cert.expiresAt).toLocaleDateString()}` : 'not configured'
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Routing jobs (24h)</CardTitle>
          </CardHeader>
          <TimeSeriesBar data={routingBars} height={80} />
          <div className="mt-3 flex flex-wrap gap-4 text-body-s text-fg-secondary">
            {routingStatuses.map((s) => (
              <span key={s}>
                {s}: <strong className="text-fg-primary">{tiles.routing24hByStatus[s] ?? 0}</strong>
              </span>
            ))}
          </div>
        </Card>

        <Link to="/admin/routing" className="block">
          <Card interactive>
            <CardHeader>
              <CardTitle>Graph circuit breakers</CardTitle>
            </CardHeader>
            {cb ? (
              <ul className="space-y-2 text-body-m">
                {(['list', 'upload', 'mail'] as const).map((fam) => (
                  <li key={fam} className="flex items-center justify-between">
                    <span className="text-fg-primary">{fam}</span>
                    <Badge tone={cbBadgeTone(cb[fam].state)}>{cb[fam].state}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-body-s text-fg-muted">unavailable</span>
            )}
          </Card>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>DLQ gauge</CardTitle>
          </CardHeader>
          <div className="flex items-center gap-4">
            <Gauge
              value={tiles.routingSnapshot?.dead ?? 0}
              max={Math.max(20, (tiles.routingSnapshot?.dead ?? 0) + 5)}
              size={100}
            />
            <div className="text-body-s text-fg-secondary">
              <div>
                Oldest:{' '}
                {tiles.routingSnapshot?.oldestDeadAgeSeconds == null
                  ? '—'
                  : `${Math.floor(tiles.routingSnapshot.oldestDeadAgeSeconds / 60)} min`}
              </div>
              <div>Completed 24h: {tiles.routingSnapshot?.completed24h ?? 0}</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
